import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/@/components/ui/button"

import { Bar, BarChart, Line, LineChart, CartesianGrid, XAxis, YAxis, PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from "recharts";
import { Skeleton } from "src/@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/@/components/ui/tooltip"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/@/components/ui/card"

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "src/@/components/ui/chart"

import MenuDatas2 from "src/components/MenuDatas2";
import { getSpent, getSpentMeses } from "src/helpers/cliente.helper";
import { validaLabelMenuDatas } from "src/utils/functions";
import { Icon } from '@iconify/react';
import v2_actionEditarCliente from "src/actions/v2/cliente/v2_actionEditarCliente";

import { CONSUMO_COMPONENT_DESIGN } from './ConsumoComponent.design';

export function ConsumoComponent({ cliente }) {
  const navigate = useNavigate();

  const [spent, setSpent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuDatas, setOpenMenuDatas] = useState(false);
  const [inicio, setInicio] = useState(new Date());
  const [final, setFinal] = useState(new Date());
  const [periodo, setPeriodo] = useState("mes_atual");
  const [useAdditionalCredits, setUseAdditionalCredits] = useState(false);
  const [spentMeses, setSpentMeses] = useState([]);
  const [loadingMeses, setLoadingMeses] = useState(true);

  useEffect(() => {
    if (!cliente?._id) return;
    handleGetSpent(inicio, final, periodo);
    handleGetSpentMeses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?._id]);

  async function handleGetSpentMeses() {
    try {
      setLoadingMeses(true);
      const response = await getSpentMeses(cliente._id);
      setSpentMeses(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMeses(false);
    }
  }

  async function handleGetSpent(startDate, endDate, periodo) {
    try {
      setLoading(true);
      startDate = startDate.toISOString().split('T')[0]
      endDate = endDate.toISOString().split('T')[0]
      let querys = `?inicio=${startDate}&final=${endDate}&periodo=${periodo}`
      const response = await getSpent(cliente._id, querys)
      setSpent(response?.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }


  async function handleChangeUseCreditsAdd(value) {
    try {
      let data = {
        scope: {
          "billing.allowCreditsAdd": value
        }
      }
      await v2_actionEditarCliente(cliente._id, data)
    } catch (error) {
      console.log(error)
    }
  }

  function goToConversasDetails() {
    const startDate = inicio.toISOString().split('T')[0];
    const endDate = final.toISOString().split('T')[0];
    const params = new URLSearchParams({
      inicio: startDate,
      final: endDate,
      periodo,
    });
    navigate(`/dashboard/conta/consumo/conversas?${params.toString()}`);
  }

  // Configuração das categorias de créditos (labels e cores compartilhadas entre os gráficos)
  const creditCategories = {
    chats: { label: 'Conversas', color: '#8b5cf6' },
    copilot: { label: 'Copilot', color: '#ec4899' },
    agents: { label: 'Agentes', color: '#a78bfa' },
    automations: { label: 'Automações', color: '#f472b6' },
    voice: { label: 'Voz', color: '#c4b5fd' }
  };

  const creditsChartConfig = useMemo(() => {
    return Object.entries(creditCategories).reduce((acc, [key, { label, color }]) => {
      acc[key] = { label, color };
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dados do gráfico de consumo de créditos por mês (últimos 6 meses)
  const monthlyChartData = useMemo(() => {
    return spentMeses.map((item) => ({
      mes: item.mes,
      chats: item.chats || 0,
      copilot: item.copilot || 0,
      agents: item.agents || 0,
      automations: item.automations || 0,
      voice: item.voice || 0
    }));
  }, [spentMeses]);

  // Dados do gráfico de distribuição de créditos no período selecionado
  const creditsDistributionData = useMemo(() => {
    const uso = spent?.credits?.uso;
    if (!uso) return [];
    return Object.entries(creditCategories)
      .map(([key, { label, color }]) => ({
        name: label,
        value: uso[key] || 0,
        fill: color
      }))
      .filter((item) => item.value > 0);
  }, [spent]);

  return (
    <div className={CONSUMO_COMPONENT_DESIGN.page.root}>
      <div className={CONSUMO_COMPONENT_DESIGN.page.stickyHeader}>
        <div className={CONSUMO_COMPONENT_DESIGN.page.container}>
          <div className={CONSUMO_COMPONENT_DESIGN.page.topbar}>
            <div className={CONSUMO_COMPONENT_DESIGN.page.topbarIdentity}>
              <div className="min-w-0">
                <h1 className={CONSUMO_COMPONENT_DESIGN.page.header.title}>Consumo</h1>
                <p className={CONSUMO_COMPONENT_DESIGN.page.header.subtitle}>Acompanhe o uso de créditos e recursos da sua conta.</p>
              </div>
            </div>
            <div className={CONSUMO_COMPONENT_DESIGN.page.topbarActions}>
              <MenuDatas2
                anchorEl={anchorEl}
                open={openMenuDatas}
                onClose={() => setAnchorEl(false)}
                inicial={inicio}
                final={final}
                content={validaLabelMenuDatas(periodo, inicio, final)}
                changeInicial={(newValue) => setInicio(newValue)}
                changeFinal={(newValue) => setFinal(newValue)}
                onSelect={(value) => {
                  setPeriodo(value);
                  setAnchorEl(null);
                }}
                onPesquisar={(startArg, endArg, periodoArg) => {
                  const s = startArg ?? inicio;
                  const e = endArg ?? final;
                  const p = periodoArg !== undefined ? periodoArg : 'null';
                  setAnchorEl(null);
                  setPeriodo(p);
                  handleGetSpent(s, e, p);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={`${CONSUMO_COMPONENT_DESIGN.page.container} ${CONSUMO_COMPONENT_DESIGN.page.pageBody}`}>
      <div className="space-y-8">
        {/* Resumo Total */}
        <Card className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Resumo Total</h3>
                <p className="text-xs text-gray-500">Período: {validaLabelMenuDatas(periodo, inicio, final)}</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-center md:text-right cursor-help">
                      <div className="flex items-center justify-center md:justify-end gap-1">
                        <p className="text-xs text-gray-600">Créditos Utilizados</p>
                        <Icon icon="material-symbols:info-outline" width={14} height={14} className="text-gray-400" />
                      </div>
                      {loading ? (
                        <Skeleton className="h-7 w-24 bg-violet-100" />
                      ) : (
                        <p className="text-lg font-bold text-violet-600">
                          {spent?.credits?.total?.use}{periodo === 'mes_atual' && `/${spent?.credits?.total?.limit}`}
                        </p>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-2">
                      <p className="font-medium">Créditos Utilizados vs Contratados</p>
                      <div className="text-sm space-y-1">
                        <p><strong>Utilizados:</strong> {spent?.credits?.total?.use || 0} créditos</p>
                        {periodo === 'mes_atual' && (
                          <p><strong>Contratados:</strong> {spent?.credits?.total?.limit || 0} créditos</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Os créditos são consumidos conforme o uso das funcionalidades de IA da plataforma.
                        </p>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>


              {periodo === 'mes_atual' && (
                <div className="flex flex-row md:flex-row gap-6">
                  {spent?.credits?.total?.credits_add > 0 && (
                    <div className="text-center md:text-right">
                      <p className="text-xs text-gray-600">Creditos Adicionais</p>
                      {loading ? (
                        <Skeleton className="h-7 w-24 bg-violet-100" />
                      ) : (
                        <p className={`text-lg font-bold ${((spent?.credits?.total?.limit - spent?.credits?.total?.use) * 0.33) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {spent?.credits?.total?.credits_add}
                        </p>
                      )}
                    </div>
                  )}
                  {/* 
                          <div className="text-center md:text-right">
                            <p className="text-xs text-gray-600">Saldo em R$</p>
                            {loading ? (
                              <Skeleton className="h-7 w-24 bg-violet-100" />
                            ) : (
                              <p className={`text-lg font-bold ${((spent?.credits?.total?.limit - spent?.credits?.total?.use) * 0.33) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                R$ {((spent?.credits?.total?.limit - spent?.credits?.total?.use) * 0.33).toFixed(2)}
                              </p>
                            )}
                          </div> */}
                </div>
              )}
            </div>
          </div>
          {/* <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="additional-credits"
                          checked={useAdditionalCredits}
                          onCheckedChange={(value) => {
                            setUseAdditionalCredits(value);
                            handleChangeUseCreditsAdd(value);
                          }}
                        />
                        <label
                          htmlFor="additional-credits"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Habilitar uso de créditos adicionais
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        Valor: R$ 0,66/crédito
                      </p>
                    </div>
                    {useAdditionalCredits && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span>Os créditos adicionais utilizados serão cobrados na próxima fatura</span>
                      </div>
                    )}
                  </div> */}
        </Card>

        {/* Consumo por Ferramenta */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Consumo por Ferramenta</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:shadow-md hover:border-violet-300 transition-all group" onClick={goToConversasDetails}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-violet-600">Conversas</h3>
                  <Icon icon="material-symbols:info-outline" width={16} height={16} className="text-gray-400" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">Créditos:</p>
                  <p className="text-sm font-bold">{spent?.credits?.uso?.chats || 0}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-6 mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToConversasDetails();
                  }}
                >
                  Ver detalhes
                </Button>
              </div>
            </Card>
            <Card className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-violet-600">Copilot</h3>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">Créditos:</p>
                  <p className="text-sm font-bold">{spent?.credits?.uso?.copilot || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-violet-600">Agentes</h3>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">Créditos:</p>
                  <p className="text-sm font-bold">{spent?.credits?.uso?.agents || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-violet-600">Automações</h3>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">Créditos:</p>
                  <p className="text-sm font-bold">{spent?.credits?.uso?.automations || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-violet-600">Voz</h3>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">Créditos:</p>
                  <p className="text-sm font-bold">{spent?.credits?.uso?.voice || 0}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Gráficos de Consumo */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900">Consumo de Créditos por Mês</CardTitle>
              <CardDescription className="text-xs text-gray-500">Últimos 6 meses</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMeses ? (
                <Skeleton className="h-72 w-full bg-violet-100" />
              ) : monthlyChartData.length === 0 ? (
                <div className="h-72 flex items-center justify-center text-sm text-gray-500">
                  Nenhum dado de consumo encontrado.
                </div>
              ) : (
                <ChartContainer className="h-72 w-full" config={creditsChartConfig}>
                  <LineChart accessibilityLayer data={monthlyChartData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="mes"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      tickFormatter={(value) => value.slice(0, 3)}
                    />
                    <YAxis tickLine={false} axisLine={false} width={30} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {Object.keys(creditCategories).map((key) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={`var(--color-${key})`}
                        strokeWidth={2}
                        dot={{ r: 3, fill: `var(--color-${key})` }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                  </LineChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl shadow-sm border border-gray-200">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900">Distribuição do Período</CardTitle>
              <CardDescription className="text-xs text-gray-500">{validaLabelMenuDatas(periodo, inicio, final)}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-72 w-full bg-violet-100" />
              ) : creditsDistributionData.length === 0 ? (
                <div className="h-72 flex items-center justify-center text-sm text-gray-500">
                  Nenhum consumo no período.
                </div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={creditsDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="value"
                        stroke="none"
                      >
                        {creditsDistributionData.map((entry, index) => (
                          <Cell key={`cell-dist-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                        iconSize={8}
                        wrapperStyle={{ fontSize: '11px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </div>
  )
}